/**
 * Activity — who did what across the ERP, newest first.
 *
 * Every successful create / update / delete (and every sign-in) is recorded by
 * the backend with the user who did it. Click an entry to see what was sent.
 */
import { useEffect, useState } from "react";
import Card from "@mui/material/Card";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import ActivityTimeline from "components/ActivityTimeline";
import { getActivityUsers } from "network/service";

const ENTITIES = [
  ["parcel", "Shipments"],
  ["clients", "Customers"],
  ["products", "Item names"],
  ["branches", "Branches"],
  ["employees", "Employees"],
  ["users", "Users & sign-ins"],
  ["vendor_apis", "Vendor APIs"],
  ["tracking_stages", "Tracking phrases"],
  ["tracking_vendors", "Tracking vendors"],
  ["carriers", "Carriers"],
  ["countries", "Countries"],
];

const ACTIONS = [
  ["create", "Created"],
  ["update", "Updated"],
  ["delete", "Deleted"],
  ["status", "Status changes"],
  ["tracking", "Tracking updates"],
  ["vendor", "Vendor bookings"],
  ["document", "Documents"],
  ["login", "Sign-ins"],
];

const EMPTY = { user: "", entity: "", action: "", from: "", to: "", search: "" };

export default function Activity() {
  const [users, setUsers] = useState([]);
  const [draft, setDraft] = useState(EMPTY);
  const [filters, setFilters] = useState(EMPTY);

  useEffect(() => {
    getActivityUsers().then((r) => setUsers(r.data || [])).catch(() => {});
  }, []);

  // Search is debounced; selects and dates apply immediately.
  useEffect(() => {
    const t = setTimeout(() => setFilters(draft), draft.search !== filters.search ? 400 : 0);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft]);

  const set = (patch) => setDraft((d) => ({ ...d, ...patch }));
  const selectSx = { minWidth: 160, height: 40 };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Card>
          <MDBox mx={2} mt={-3} py={2} px={2} variant="gradient" bgColor="info" borderRadius="lg" coloredShadow="info">
            <MDTypography variant="h6" color="white">Activity</MDTypography>
          </MDBox>

          <MDBox px={3} pt={3} display="flex" gap={1.5} flexWrap="wrap" alignItems="center">
            <Select size="small" displayEmpty value={draft.user} sx={selectSx} onChange={(e) => set({ user: e.target.value })}>
              <MenuItem value="">All users</MenuItem>
              {users.map((u) => (
                <MenuItem key={u._id} value={u._id}>{u.name} ({u.count})</MenuItem>
              ))}
            </Select>
            <Select size="small" displayEmpty value={draft.entity} sx={selectSx} onChange={(e) => set({ entity: e.target.value })}>
              <MenuItem value="">Everything</MenuItem>
              {ENTITIES.map(([k, l]) => <MenuItem key={k} value={k}>{l}</MenuItem>)}
            </Select>
            <Select size="small" displayEmpty value={draft.action} sx={selectSx} onChange={(e) => set({ action: e.target.value })}>
              <MenuItem value="">All actions</MenuItem>
              {ACTIONS.map(([k, l]) => <MenuItem key={k} value={k}>{l}</MenuItem>)}
            </Select>
            <MDInput type="date" label="From" InputLabelProps={{ shrink: true }} value={draft.from} onChange={(e) => set({ from: e.target.value })} />
            <MDInput type="date" label="To" InputLabelProps={{ shrink: true }} value={draft.to} onChange={(e) => set({ to: e.target.value })} />
            <MDInput placeholder="Search activity" value={draft.search} sx={{ minWidth: 220 }} onChange={(e) => set({ search: e.target.value })} />
            {JSON.stringify(draft) !== JSON.stringify(EMPTY) && (
              <MDButton variant="text" color="secondary" onClick={() => setDraft(EMPTY)}>Clear</MDButton>
            )}
          </MDBox>

          <MDBox px={3} py={3}>
            <ActivityTimeline filters={filters} />
          </MDBox>
        </Card>
      </MDBox>
    </DashboardLayout>
  );
}
